import { Component } from '@theme/component';

/**
 * UGC Feed — horizontal "peek" carousel of social video posts with product CTAs.
 *
 * Native CSS scroll-snap drives scrolling. This component:
 *   • starts at a centered peek offset (so both edges are cropped on load),
 *   • scrolls one card on prev/next,
 *   • sizes/positions the progress thumb (mobile footer),
 *   • disables header + footer arrows at the scroll ends,
 *   • plays muted videos only while their card is in view.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} [progress]
 *
 * @extends {Component<Refs>}
 */
class UgcFeedComponent extends Component {
  requiredRefs = ['scroller'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  /** @type {number} */
  #frame = 0;

  /** @type {boolean} */
  #userInteracted = false;

  /** @type {boolean} */
  #programmaticScroll = false;

  /** @type {number} */
  #centeredOffset = 0;

  connectedCallback() {
    super.connectedCallback();

    const { scroller } = this.refs;
    scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    scroller.addEventListener('pointerdown', this.#onUserInteract, { passive: true });
    scroller.addEventListener('touchstart', this.#onUserInteract, { passive: true });
    scroller.addEventListener('wheel', this.#onUserInteract, { passive: true });

    this.#resizeObserver = new ResizeObserver(() => {
      this.#onScroll();
      if (!this.#userInteracted) this.#centerInitial(true);
    });
    this.#resizeObserver.observe(scroller);

    this.#observeVideos();
    this.#centerInitial(true);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { scroller } = this.refs;
    scroller.removeEventListener('scroll', this.#onScroll);
    scroller.removeEventListener('pointerdown', this.#onUserInteract);
    scroller.removeEventListener('touchstart', this.#onUserInteract);
    scroller.removeEventListener('wheel', this.#onUserInteract);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
    cancelAnimationFrame(this.#frame);
  }

  next() {
    this.#userInteracted = true;
    this.#scrollByCards(1);
  }

  previous() {
    this.#userInteracted = true;
    this.#scrollByCards(-1);
  }

  #onUserInteract = () => {
    this.#userInteracted = true;
  };

  /** @param {number} direction - 1 forward, -1 backward */
  #scrollByCards(direction) {
    const { scroller } = this.refs;
    const step = this.#slideStep();
    this.#programmaticScroll = true;
    scroller.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  #slideStep() {
    const { scroller } = this.refs;
    const slide = scroller.querySelector('[data-slide]');
    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    return slide ? slide.getBoundingClientRect().width + gap : scroller.clientWidth;
  }

  /**
   * Offset so the fractional “peek” from --ugc-per-view is split across both
   * edges (e.g. 4.5 → half-card cropped left and right). Matches the Figma frame.
   * @param {boolean} [force]
   */
  #centerInitial(force = false) {
    const { scroller } = this.refs;
    if (!force && this.#userInteracted) return;

    const slides = scroller.querySelectorAll('[data-slide]');
    if (slides.length < 2) return;

    const step = this.#slideStep();
    if (step <= 0) return;

    const perView = parseFloat(getComputedStyle(this).getPropertyValue('--ugc-per-view')) || 4.5;
    const fractional = perView - Math.floor(perView);
    const offset = fractional > 0 ? (fractional / 2) * step : step * 0.25;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    if (maxScroll <= 1) return;

    this.#centeredOffset = Math.min(offset, maxScroll * 0.5);
    this.#programmaticScroll = true;

    const snapType = scroller.style.scrollSnapType;
    scroller.style.scrollSnapType = 'none';
    scroller.scrollLeft = this.#centeredOffset;

    requestAnimationFrame(() => {
      scroller.style.scrollSnapType = snapType;
      this.#programmaticScroll = false;
      this.#sync();
    });
  }

  #observeVideos() {
    const videos = this.querySelectorAll('video.ugc-feed__media--video');
    if (!videos.length) return;

    this.#videoObserver?.disconnect();
    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      {
        root: this.refs.scroller,
        threshold: 0.35,
      }
    );

    videos.forEach((video) => {
      video.muted = true;
      video.setAttribute('playsinline', '');
      this.#videoObserver?.observe(video);
    });
  }

  #onScroll = () => {
    if (!this.#programmaticScroll && Math.abs(this.refs.scroller.scrollLeft - this.#centeredOffset) > 2) {
      this.#userInteracted = true;
    }
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const { scroller, progress } = this.refs;
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    const maxScroll = scrollWidth - clientWidth;
    const atStart = scrollLeft <= 1;
    const atEnd = maxScroll <= 1 || scrollLeft >= maxScroll - 1;

    if (progress) {
      const size = scrollWidth > 0 ? Math.min(100, (clientWidth / scrollWidth) * 100) : 100;
      const offset = scrollWidth > 0 ? (scrollLeft / scrollWidth) * 100 : 0;
      progress.style.width = `${size}%`;
      progress.style.insetInlineStart = `${offset}%`;
    }

    this.querySelectorAll('[data-ugc-arrow="previous"]').forEach((button) => {
      button.toggleAttribute('disabled', atStart);
    });
    this.querySelectorAll('[data-ugc-arrow="next"]').forEach((button) => {
      button.toggleAttribute('disabled', atEnd);
    });
  }
}

if (!customElements.get('ugc-feed-component')) {
  customElements.define('ugc-feed-component', UgcFeedComponent);
}
